import { Component, DestroyRef, effect, inject, input, output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ProductAdvice, ProductAdviceAnswers } from '@models/product-advice';
import { OrderState } from 'app/order/models';

export type AnswerOption<T extends string = string> = {
    label: string;
    value: T | null;
};

export type QuestionItem = {
    controlName: string;
    question: string;
    answerOptions: AnswerOption[];
};

@Component({
    selector: 'app-product-advice',
    templateUrl: './product-advice.component.html',
    styleUrl: './product-advice.component.scss',
    imports: [ReactiveFormsModule],
})
export class ProductAdviceComponent {
    public questionSet = input<QuestionItem[]>([]);
    public initialValues = input<ProductAdvice>();

    public submit = output<void>();
    public answersUpdated = output<{
        answers: ProductAdviceAnswers;
        complete: boolean;
    }>();

    protected adviceForm: FormGroup;

    protected getAdvice = signal<boolean>(true);

    private destroyRef = inject(DestroyRef);

    public constructor() {
        // initialize form and restore values with previous questionair
        effect(() => {
            const questions = this.questionSet();
            const initialValues = this.initialValues();

            untracked(() => {
                this.adviceForm = this.buildForm(questions);

                // restore previous answers
                if (initialValues?.answers) {
                    this.adviceForm.patchValue(initialValues.answers, { emitEvent: false });
                }

                // restore mode
                if (initialValues?.mode) {
                    this.toggleAdvice(initialValues.mode === 'ADVICE', false);
                }

                // restore waiverConfirmed
                if (initialValues?.waiverConfirmed) {
                    this.adviceForm.controls.waiverConfirmed.patchValue(initialValues.waiverConfirmed, {
                        emitEvent: false,
                    });
                }
            });

            this.adviceForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
                const answers: ProductAdviceAnswers = {};

                for (const question of questions) {
                    const answer = value[question.controlName];

                    if (answer !== null && answer !== undefined) {
                        answers[question.controlName] = answer;
                    }
                }

                this.answersUpdated.emit({ answers, complete: this.adviceForm.valid && this.getAdvice() });
            });
        });
    }

    private buildForm(questions: QuestionItem[]): FormGroup {
        const controls: Record<string, FormControl> = {
            getAdvice: new FormControl<boolean>(true, Validators.required),
            waiverConfirmed: new FormControl<boolean>({ value: false, disabled: true }, Validators.requiredTrue),
        };

        for (const q of questions) {
            controls[q.controlName] = new FormControl<string | null>(null, Validators.required);
        }

        return new FormGroup(controls);
    }

    protected toggleAdvice(advice: boolean, emitEvent: boolean = true): void {
        this.getAdvice.set(advice);
        this.adviceForm.controls.getAdvice.patchValue(advice, { emitEvent: emitEvent });
        if (!advice) {
            this.adviceForm.controls.waiverConfirmed.enable();
            for (const q of this.questionSet()) {
                this.adviceForm.controls[q.controlName].disable();
            }
        } else {
            this.adviceForm.controls.waiverConfirmed.patchValue(false);
            this.adviceForm.controls.waiverConfirmed.disable();
            for (const q of this.questionSet()) {
                this.adviceForm.controls[q.controlName].enable();
            }
        }
    }

    public getData(): Partial<OrderState> {
        const mode = this.getAdvice() ? 'ADVICE' : 'WAIVER';
        const formValue = this.adviceForm.value;

        if (mode === 'WAIVER') {
            return {
                advice: {
                    mode,
                    waiverConfirmed: formValue.waiverConfirmed,
                },
            };
        }

        const answers: ProductAdviceAnswers = {};
        for (const q of this.questionSet()) {
            answers[q.controlName] = formValue[q.controlName];
        }

        return { advice: { mode, answers } };
    }
}
